import './App.css';
import React, { useState, useEffect } from 'react';
import Amplify, { Storage } from 'aws-amplify';
import awsconfig from './aws-exports';
import Dropdown from 'react-dropdown';
import 'react-dropdown/style.css';

const X_PROFILE_URL = 'https://x.com/The_Iditarod';

const thisyear = new Date().getFullYear();
const options = [];
for (let o = thisyear; o > 2012; o--) {
  options.push(o);
}

Amplify.configure(awsconfig);

function parseCSV(csv) {
  const lines = csv.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length === 0) return { headers: [], rows: [] };
  const parseRow = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"') {
        inQuotes = !inQuotes;
      } else if ((c === ',' && !inQuotes) || c === '\r') {
        result.push(current.trim());
        current = '';
      } else {
        current += c;
      }
    }
    result.push(current.trim());
    return result;
  };
  const headers = parseRow(lines[0]);
  const rows = lines.slice(1).map(parseRow);
  return { headers, rows };
}

const COL_ALIASES = {
  'In_Time': ['In_Time', 'Time In'],
  'In_Dogs': ['In_Dogs', 'Dogs In'],
  'Out_Time': ['Out_Time', 'Time Out'],
  'Out_Dogs': ['Out_Dogs', 'Dogs Out'],
};

function col(row, headers, name) {
  const names = COL_ALIASES[name] || [name];
  for (const n of names) {
    const idx = headers.findIndex((h) => h.trim() === n);
    if (idx >= 0 && row[idx]) return row[idx];
  }
  return '';
}

function colByPattern(row, headers, pattern) {
  const idx = headers.findIndex((h) => pattern.test(h));
  return idx >= 0 ? (row[idx] || '') : '';
}

const FRIENDLY_LABELS = {
  'In_Time': 'In',
  'Out_Time': 'Out',
  'In_Dogs': 'Dogs In',
  'Out_Dogs': 'Dogs Out',
  'Rest In Chkpt': 'Rest',
  'Time Enroute': 'Enroute',
  'Previous_Checkpoint': 'From',
  'Previous_Time Out': 'Prev Departure',
  'Previous_Speed(mph)': 'Speed (mph)',
  'Average Speed': 'Avg Speed',
  'Total Race Time': 'Race Time',
};

function friendlyLabel(raw) {
  return FRIENDLY_LABELS[raw] || raw.replace(/_/g, ' ');
}

const CORE_FIELDS = ['Position', 'Player', 'Musher', 'Pos', 'Checkpoint', 'Bib'];

function detectSectionType(csvString) {
  const { headers, rows } = parseCSV(csvString);
  if (rows.length === 0) return 'empty';
  const hSet = new Set(headers.map((h) => h.trim()));
  if (hSet.has('Total Race Time') || hSet.has('Average Speed')) return 'finished';
  if (hSet.has('Status')) {
    const statusIdx = headers.findIndex((h) => h.trim() === 'Status');
    const hasAnyScratch = rows.some((r) => {
      const v = (r[statusIdx] || '').trim().toLowerCase();
      return v === 'scratched' || v === 'withdrawn';
    });
    if (hasAnyScratch) return 'scratched';
  }
  if (!hSet.has('Checkpoint') && !hSet.has('In_Time')) return 'unknown';
  return 'racing';
}

function sectionTitle(type) {
  switch (type) {
    case 'finished': return 'Finished';
    case 'racing': return 'Racing';
    case 'scratched': return 'Out of Race';
    default: return 'Standings';
  }
}

function buildChartCsv(standings) {
  const CHART_HEADER = 'Player,Pos,Musher,Checkpoint,In_Time,In_Dogs,Out_Time,Out_Dogs,Rest In Chkpt,Time Enroute,Previous_Checkpoint,Previous_Time Out,Previous_Speed(mph),LayoverCompleted_8hr,LayoverCompleted_24hr,Status';
  const dataRows = [];

  for (const csv of standings) {
    const t = detectSectionType(csv);
    if (t === 'finished') {
      const { headers, rows } = parseCSV(csv);
      rows.forEach((row) => {
        const player = col(row, headers, 'Player');
        const pos = col(row, headers, 'Pos');
        const musher = col(row, headers, 'Musher');
        const checkpoint = col(row, headers, 'Checkpoint') || 'Nome';
        const timeIn = col(row, headers, 'Time In') || col(row, headers, 'In_Time') || '';
        const dogsIn = col(row, headers, 'Dogs In') || col(row, headers, 'In_Dogs') || '';
        dataRows.push([player, pos, musher, checkpoint, timeIn, dogsIn, '', '', '', '', '', '', '', '', '', ''].join(','));
      });
    } else if (t === 'racing') {
      const lines = csv.trim().split(/\r?\n/).filter(Boolean);
      dataRows.push(...lines.slice(1));
    }
  }

  if (dataRows.length === 0) return standings[0] || '';
  return CHART_HEADER + '\n' + dataRows.join('\n');
}

function cleanDogCount(val) {
  if (!val) return '';
  const n = parseFloat(val);
  return Number.isFinite(n) ? String(Math.round(n)) : val;
}

/* ============ Checkpoint distance tables (miles from start) ============ */
const NORTHERN_ROUTE = {
  total: 975,
  cp: {
    'Anchorage': 0, 'Campbell Airstrip': 11, 'Willow': 11,
    'Yentna': 53, 'Yentna Station': 53, 'Skwentna': 83,
    'Finger Lake': 118, 'Rainy Pass': 153, 'Rohn': 188,
    'Nikolai': 263, 'McGrath': 311, 'Takotna': 329,
    'Ophir': 352, 'Cripple': 425, 'Ruby': 495,
    'Galena': 545, 'Nulato': 582, 'Kaltag': 629,
    'Unalakleet': 714, 'Shaktoolik': 754, 'Koyuk': 804,
    'Elim': 852, 'Golovin': 880, 'White Mountain': 898,
    'Safety': 953, 'Nome': 975,
  },
};

const SOUTHERN_ROUTE = {
  total: 998,
  cp: {
    'Anchorage': 0, 'Campbell Airstrip': 11, 'Willow': 11,
    'Yentna': 53, 'Yentna Station': 53, 'Skwentna': 83,
    'Finger Lake': 118, 'Rainy Pass': 153, 'Rohn': 188,
    'Nikolai': 263, 'McGrath': 311, 'Takotna': 329,
    'Ophir': 352, 'Iditarod': 432, 'Shageluk': 490,
    'Anvik': 514, 'Grayling': 537, 'Eagle Island': 599,
    'Kaltag': 652, 'Unalakleet': 738, 'Shaktoolik': 778,
    'Koyuk': 828, 'Elim': 876, 'Golovin': 904,
    'White Mountain': 922, 'Safety': 977, 'Nome': 998,
  },
};

const FAIRBANKS_2025 = {
  total: 1128,
  cp: {
    'Fairbanks': 0, 'Nenana': 52, 'Manley': 137,
    'Tanana': 202, 'Ruby': 319, 'Galena': 369,
    'Nulato': 420,
    'Kaltag': 456, 'Kaltag 1': 456,
    'Eagle Island': 526, 'Eagle Island 1': 526,
    'Grayling': 582, 'Grayling 1': 582,
    'Anvik': 603, 'Shageluk': 631,
    'Grayling 2': 659, 'Eagle Island 2': 715,
    'Kaltag 2': 785,
    'Unalakleet': 866, 'Shaktoolik': 908, 'Koyuk': 958,
    'Elim': 1008, 'White Mountain': 1057,
    'Safety': 1106, 'Nome': 1128,
  },
};

function getRouteForYear(year) {
  if (year === 2025) return FAIRBANKS_2025;
  if (year % 2 === 0) return NORTHERN_ROUTE;
  return SOUTHERN_ROUTE;
}

function getProgress(checkpoint, year, extraMiles) {
  if (!checkpoint) return null;
  const route = getRouteForYear(year);
  const cpDist = route.cp[checkpoint];
  if (cpDist === undefined) return null;

  let dist = cpDist;
  if (extraMiles && extraMiles > 0) {
    // Find next checkpoint distance to cap the estimate
    const allDists = [...new Set(Object.values(route.cp))].sort((a, b) => a - b);
    const nextDist = allDists.find((d) => d > cpDist);
    const cap = nextDist !== undefined ? nextDist : route.total;
    dist = Math.min(cpDist + extraMiles, cap);
  }

  const pct = Math.min(Math.round((dist / route.total) * 100), 100);
  return { pct, miles: Math.round(dist), total: route.total };
}

function estimateExtraMiles(row, headers, year) {
  const outTimeVal = col(row, headers, 'Out_Time');
  if (!outTimeVal) return 0;
  const speed = parseFloat(col(row, headers, 'Previous_Speed(mph)'));
  if (!Number.isFinite(speed) || speed <= 0) return 0;
  if (year !== thisyear) return 0;
  const outTime = parseRaceTime(outTimeVal, year);
  if (!outTime) return 0;
  const now = getAlaskaNow();
  const hoursOnTrail = (now - outTime) / 3600000;
  if (hoursOnTrail <= 0) return 0;
  return speed * hoursOnTrail;
}

function getAlaskaNow() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Anchorage' }));
}

function parseRaceTime(timeStr, year) {
  if (!timeStr || !timeStr.trim()) return null;
  const match = timeStr.trim().match(/(\d+)\/(\d+)\s+(\d+):(\d+):(\d+)/);
  if (!match) return null;
  const [, month, day, hour, min, sec] = match.map(Number);
  return new Date(year, month - 1, day, hour, min, sec);
}

function formatElapsed(ms) {
  if (!Number.isFinite(ms) || ms < 0) return '';
  const totalMin = Math.floor(ms / 60000);
  if (totalMin < 1) return 'just now';
  const hours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

/* ============ Trail Map Chart ============ */

function getUniqueCheckpoints(route) {
  const seen = new Map();
  for (const [name, dist] of Object.entries(route.cp)) {
    if (!seen.has(dist) || name.length < seen.get(dist).length) {
      seen.set(dist, name);
    }
  }
  return [...seen.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([dist, name]) => ({ name, dist }));
}

function RaceChart({ csvString, year }) {
  const [activePlayer, setActivePlayer] = useState(null);
  const { headers, rows } = parseCSV(csvString);
  if (rows.length < 2) return null;

  const route = getRouteForYear(year);
  const allCps = getUniqueCheckpoints(route);

  const has8hrCol = headers.some((h) => /8hr/i.test(h));
  const has24hrCol = headers.some((h) => /24hr/i.test(h));

  const hiddenInTooltip = new Set([...CORE_FIELDS, 'In_Dogs', 'Out_Dogs']);
  headers.forEach((h) => { if (/8hr|24hr/i.test(h)) hiddenInTooltip.add(h.trim()); });
  const tooltipHeaders = headers.filter((h) => !hiddenInTooltip.has(h.trim()));

  // Compute MyGame3G rank (players sorted by Iditarod Pos)
  const allEntries = rows
    .map((row) => ({
      player: col(row, headers, 'Player'),
      pos: parseInt(col(row, headers, 'Pos'), 10) || 999,
      row,
    }))
    .filter((e) => e.player)
    .sort((a, b) => a.pos - b.pos);
  const gameRankMap = new Map();
  allEntries.forEach((e, i) => { gameRankMap.set(e.player, i + 1); });

  // Group players by checkpoint, ordered by overall race position
  const groups = new Map();
  rows.forEach((row) => {
    const player = col(row, headers, 'Player');
    const checkpoint = col(row, headers, 'Checkpoint');
    const pos = parseInt(col(row, headers, 'Pos'), 10) || 999;
    const dist = route.cp[checkpoint];
    if (dist === undefined || !player) return;
    const outTimeVal = col(row, headers, 'Out_Time');
    const departed = !!outTimeVal;
    const gameRank = gameRankMap.get(player) || 999;
    if (!groups.has(checkpoint)) groups.set(checkpoint, []);
    groups.get(checkpoint).push({ player, pos, gameRank, dist, departed, row });
  });
  groups.forEach((entries) => entries.sort((a, b) => a.pos - b.pos));

  // Checkpoints with players, sorted furthest-first (Nome → Start)
  const activeCps = [...groups.entries()]
    .map(([name, entries]) => ({ name, dist: entries[0].dist, entries }))
    .sort((a, b) => b.dist - a.dist);

  // Count skipped checkpoints between each active stop
  const cpDistSet = new Set(allCps.map((c) => c.dist));
  function skippedBetween(distA, distB) {
    const lo = Math.min(distA, distB);
    const hi = Math.max(distA, distB);
    let count = 0;
    for (const d of cpDistSet) {
      if (d > lo && d < hi) count++;
    }
    return count;
  }

  const pctFor = (d) => Math.round((d / route.total) * 100);

  // Reusable pill renderer
  function renderPill({ player, pos, gameRank, row }, isDeparted) {
    const musher = col(row, headers, 'Musher');
    const cp = col(row, headers, 'Checkpoint');
    const inTimeVal = col(row, headers, 'In_Time');
    const outTimeVal = col(row, headers, 'Out_Time');
    const atCp = !!inTimeVal && !outTimeVal;
    const leftCp = !!inTimeVal && !!outTimeVal;
    const isNome = cp && cp.toLowerCase() === 'nome';
    const outDogs = col(row, headers, 'Out_Dogs');
    const inDogs = col(row, headers, 'In_Dogs');
    const dogCount = cleanDogCount((leftCp ? outDogs : inDogs) || outDogs || inDogs);
    const done8hr = has8hrCol && colByPattern(row, headers, /8hr/i).includes('✔');
    const done24hr = has24hrCol && colByPattern(row, headers, /24hr/i).includes('✔');
    const extra = leftCp ? estimateExtraMiles(row, headers, year) : 0;
    const progress = getProgress(cp, year, extra);
    const isCurrentYear = year === thisyear;
    const alaskaNow = isCurrentYear ? getAlaskaNow() : null;
    let elapsed = '';
    if (alaskaNow && cp && !isNome) {
      const refTime = parseRaceTime(atCp ? inTimeVal : outTimeVal, year);
      if (refTime) elapsed = formatElapsed(alaskaNow - refTime);
    }

    const pillClass = [
      'trail-pill',
      gameRank <= 3 ? 'top3' : '',
      isNome ? 'finished' : atCp ? 'resting' : '',
      isDeparted ? 'on-trail' : '',
    ].filter(Boolean).join(' ');

    return (
      <span
        key={player}
        className="trail-pill-wrap"
        onMouseEnter={() => setActivePlayer(player)}
        onMouseLeave={() => setActivePlayer(null)}
        onClick={(e) => {
          e.stopPropagation();
          setActivePlayer(activePlayer === player ? null : player);
        }}
      >
        <span className={pillClass}>
          {isNome && <span className="pill-finish">🏁{gameRank}</span>}
          {!isNome && atCp && <span className="pill-dot" />}
          {isDeparted && <span className="pill-arrow">→</span>}
          {player}
          {isDeparted && progress && (
            <span className="pill-pct">{progress.pct}%</span>
          )}
        </span>
        {activePlayer === player && (
          <div className="trail-tooltip">
            <div className="trail-tooltip-header">
              <strong>{player}</strong>
              <span className="trail-tooltip-sub">
                {musher} · MyGame #{gameRank} · Iditarod #{pos}
              </span>
            </div>
            <div className="trail-tooltip-indicators">
              {isNome ? (
                <span className="tt-status finished">🏁 Finished</span>
              ) : atCp ? (
                <span className="tt-status resting">
                  <span className="status-dot" /> At {cp}
                  {elapsed && <span className="status-time">{elapsed}</span>}
                </span>
              ) : leftCp ? (
                <span className="tt-status on-trail">
                  → Left {cp}
                  {elapsed && <span className="status-time">{elapsed} ago</span>}
                </span>
              ) : null}
              {dogCount && <span className="tt-dogs">🐕 {dogCount}</span>}
              {(has8hrCol || has24hrCol) && (
                <span className="tt-breaks">
                  {has8hrCol && (
                    <span className={`break-badge${done8hr ? ' done' : ''}`}>
                      8h{done8hr ? ' ✓' : ''}
                    </span>
                  )}
                  {has24hrCol && (
                    <span className={`break-badge${done24hr ? ' done' : ''}`}>
                      24h{done24hr ? ' ✓' : ''}
                    </span>
                  )}
                </span>
              )}
            </div>
            {progress && (
              <div className="tt-progress">
                <div className="progress-track">
                  <div
                    className={`progress-fill${progress.pct >= 100 ? ' complete' : ''}`}
                    style={{ width: `${Math.min(progress.pct, 100)}%` }}
                  />
                </div>
                <span className="progress-label">{progress.pct}%</span>
              </div>
            )}
            <div className="trail-tooltip-grid">
              {tooltipHeaders.map((h) => {
                const val = col(row, headers, h);
                if (!val) return null;
                return (
                  <div key={h} className="trail-tooltip-item">
                    <span className="detail-label">{friendlyLabel(h)}</span>
                    <span className="detail-value">{val}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </span>
    );
  }

  return (
    <section className="section-card chart-card">
      <h2 className="section-title">Race Progress</h2>
      <div className="vtrail" onClick={() => setActivePlayer(null)}>
        {/* Nome finish line if no one is there yet */}
        {activeCps.length > 0 && activeCps[0].name.toLowerCase() !== 'nome' && (
          <div className="vtrail-stop finish-line">
            <div className="vtrail-rail"><span className="vtrail-node finish" /></div>
            <div className="vtrail-body">
              <span className="vtrail-cp-name">Nome 🏁</span>
              <span className="vtrail-dist">
                <span className="vtrail-miles">{route.total} mi</span>
                <span className="vtrail-pct">Finish</span>
              </span>
            </div>
          </div>
        )}

        {activeCps.map(({ name, dist, entries }, idx) => {
          const skipped = idx > 0 ? skippedBetween(activeCps[idx - 1].dist, dist) : 0;
          const departedCount = entries.filter((e) => e.departed).length;
          const restingCount = entries.length - departedCount;

          return (
            <React.Fragment key={name}>
              {skipped > 0 && (
                <div className="vtrail-gap">
                  <div className="vtrail-rail">
                    {Array.from({ length: Math.min(skipped, 4) }).map((_, i) => (
                      <span key={i} className="vtrail-gap-dot" />
                    ))}
                  </div>
                  <span className="vtrail-gap-label">
                    {skipped} checkpoint{skipped > 1 ? 's' : ''}
                  </span>
                </div>
              )}

              <div className={`vtrail-stop${name.toLowerCase() === 'nome' ? ' at-nome' : ''}`}>
                <div className="vtrail-rail">
                  <span className={`vtrail-node${entries.length > 5 ? ' big' : ''}`} />
                </div>
                <div className="vtrail-body">
                  <div className="vtrail-header">
                    <span className="vtrail-cp-name">{name}</span>
                    <span className="vtrail-dist">
                      <span className="vtrail-miles">{dist} mi</span>
                      <span className="vtrail-pct">{pctFor(dist)}%</span>
                      {departedCount > 0 && restingCount > 0 && (
                        <span className="vtrail-split">
                          {restingCount} here · {departedCount} departed
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="vtrail-pills">
                    {entries.map((entry) => renderPill(entry, entry.departed))}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}

        {/* Start marker */}
        <div className="vtrail-stop start-line">
          <div className="vtrail-rail"><span className="vtrail-node start" /></div>
          <div className="vtrail-body">
            <span className="vtrail-cp-name">Start</span>
          </div>
        </div>
      </div>
    </section>
  );
}

function StandingsTable({ csvString, isFirst, startRank = 0, year }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [, setTick] = useState(0);
  const { headers, rows } = parseCSV(csvString);

  // Auto-refresh elapsed times every 60s for current year
  useEffect(() => {
    if (year !== thisyear) return;
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [year]);

  if (rows.length === 0) return null;

  const hasCheckpoint = headers.some((h) => h.trim() === 'Checkpoint');
  const posIdx = headers.findIndex((h) => h.trim() === 'Pos');
  const isCurrentYear = year === thisyear;
  const alaskaNow = isCurrentYear ? getAlaskaNow() : null;

  const has8hrCol = headers.some((h) => /8hr/i.test(h));
  const has24hrCol = headers.some((h) => /24hr/i.test(h));
  const hasInTimeCol = headers.some((h) => h.trim() === 'In_Time' || h.trim() === 'Time In');

  let displayRows = rows;
  let showPosition = false;

  if (isFirst && hasCheckpoint && posIdx >= 0) {
    showPosition = true;
    const numericPos = (row) => {
      const n = parseInt(row[posIdx], 10);
      return Number.isNaN(n) ? Infinity : n;
    };
    displayRows = [...rows].sort((a, b) => numericPos(a) - numericPos(b));
  }

  // Columns already represented in the row summary — hide from expanded details
  const hiddenInDetails = new Set([...CORE_FIELDS, 'In_Dogs', 'Out_Dogs']);
  headers.forEach((h) => {
    if (/8hr|24hr/i.test(h)) hiddenInDetails.add(h.trim());
  });
  const detailHeaders = headers.filter((h) => !hiddenInDetails.has(h.trim()));

  return (
    <div className="standings-list">
      {displayRows.map((row, ri) => {
        const player = col(row, headers, 'Player');
        const musher = col(row, headers, 'Musher');
        const pos = col(row, headers, 'Pos');
        const checkpoint = col(row, headers, 'Checkpoint');
        const position = showPosition ? startRank + ri + 1 : null;
        const isExpanded = expandedRow === ri;
        const hasDetails = detailHeaders.length > 0;

        // Break status
        const done8hr = has8hrCol && colByPattern(row, headers, /8hr/i).includes('✔');
        const done24hr = has24hrCol && colByPattern(row, headers, /24hr/i).includes('✔');

        // Checkpoint status: at checkpoint (resting) vs on trail (left)
        const inTimeVal = col(row, headers, 'In_Time');
        const outTimeVal = col(row, headers, 'Out_Time');
        const atCheckpoint = hasInTimeCol && !!inTimeVal && !outTimeVal;
        const leftCheckpoint = hasInTimeCol && !!inTimeVal && !!outTimeVal;

        const isFinished = checkpoint && checkpoint.toLowerCase() === 'nome';
        const extra = leftCheckpoint ? estimateExtraMiles(row, headers, year) : 0;
        const progress = getProgress(checkpoint, year, extra);

        // Current dog count: use Out_Dogs if they left, otherwise In_Dogs
        const outDogs = col(row, headers, 'Out_Dogs');
        const inDogs = col(row, headers, 'In_Dogs');
        const dogCount = cleanDogCount((leftCheckpoint ? outDogs : inDogs) || outDogs || inDogs);

        let elapsed = '';
        if (alaskaNow && checkpoint && !isFinished) {
          const refTime = parseRaceTime(atCheckpoint ? inTimeVal : outTimeVal, year);
          if (refTime) elapsed = formatElapsed(alaskaNow - refTime);
        }

        let statusEl = null;
        if (checkpoint && hasInTimeCol && inTimeVal) {
          if (isFinished) {
            const totalTime = col(row, headers, 'Total Race Time');
            statusEl = (
              <span className="row-status finished">
                <span className="status-flag">🏁</span>
                <span>Finished{totalTime ? ` — ${totalTime}` : ''}</span>
              </span>
            );
          } else if (atCheckpoint) {
            statusEl = (
              <span className="row-status resting">
                <span className="status-dot" />
                <span>At {checkpoint}</span>
                {elapsed && <span className="status-time">{elapsed}</span>}
              </span>
            );
          } else if (leftCheckpoint) {
            statusEl = (
              <span className="row-status on-trail">
                <span className="status-arrow">→</span>
                <span>Left {checkpoint}</span>
                {elapsed && <span className="status-time">{elapsed} ago</span>}
              </span>
            );
          }
        } else if (checkpoint) {
          const statusVal = col(row, headers, 'Status');
          const isScratch = statusVal && /scratch|withdraw/i.test(statusVal);
          statusEl = (
            <span className={`row-status ${isScratch ? 'scratched' : 'neutral'}`}>
              {isScratch && <span className="status-x">✕</span>}
              <span>{isScratch ? `${statusVal} — ${checkpoint}` : checkpoint}</span>
            </span>
          );
        }

        return (
          <div
            key={ri}
            className={`standing-row${isExpanded ? ' expanded' : ''}${position === 1 ? ' leader' : ''}`}
            onClick={() => hasDetails && setExpandedRow(isExpanded ? null : ri)}
            role={hasDetails ? 'button' : undefined}
            tabIndex={hasDetails ? 0 : undefined}
            onKeyDown={(e) => {
              if (hasDetails && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                setExpandedRow(isExpanded ? null : ri);
              }
            }}
          >
            <div className="row-main">
              {position !== null && (
                <span className={`row-position${position <= 3 ? ' top3' : ''}`}>
                  {position}
                </span>
              )}
              <div className="row-content">
                <div className="row-line1">
                  <span className="row-player">{player}</span>
                  <div className="row-line1-right">
                    {(has8hrCol || has24hrCol) && (
                      <div className="break-badges">
                        {has8hrCol && (
                          <span
                            className={`break-badge${done8hr ? ' done' : ''}`}
                            title={done8hr ? '8-hour break completed' : '8-hour break pending'}
                          >
                            8h{done8hr ? ' ✓' : ''}
                          </span>
                        )}
                        {has24hrCol && (
                          <span
                            className={`break-badge${done24hr ? ' done' : ''}`}
                            title={done24hr ? '24-hour break completed' : '24-hour break pending'}
                          >
                            24h{done24hr ? ' ✓' : ''}
                          </span>
                        )}
                      </div>
                    )}
                    {pos && <span className="row-pos">#{pos}</span>}
                  </div>
                </div>
                <div className="row-line2">
                  <span className="row-musher">{musher}</span>
                  {statusEl}
                  {dogCount && (
                    <span className="row-dogs" title={`${dogCount} dogs`}>
                      <span className="dog-icon" aria-hidden>🐕</span>{dogCount}
                    </span>
                  )}
                </div>
                {progress && (
                  <div
                    className="row-progress"
                    title={`${progress.miles} of ${progress.total} mi (${progress.pct}%)`}
                  >
                    <div className="progress-track">
                      <div
                        className={`progress-fill${progress.pct >= 100 ? ' complete' : ''}`}
                        style={{ width: `${Math.min(progress.pct, 100)}%` }}
                      />
                    </div>
                    <span className="progress-label">{progress.pct}%</span>
                  </div>
                )}
              </div>
              {hasDetails && (
                <span className={`row-chevron${isExpanded ? ' open' : ''}`} aria-hidden>
                  &#x25BE;
                </span>
              )}
            </div>
            {isExpanded && hasDetails && (
              <div className="row-details">
                {detailHeaders.map((h) => {
                  const val = col(row, headers, h);
                  if (!val) return null;
                  return (
                    <div key={h} className="detail-item">
                      <span className="detail-label">{friendlyLabel(h)}</span>
                      <span className="detail-value">{val}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const YEAR_STORAGE_KEY = 'mygame3g-year';

function getStoredYear() {
  try {
    const stored = localStorage.getItem(YEAR_STORAGE_KEY);
    const y = parseInt(stored, 10);
    if (Number.isFinite(y) && y >= 2013 && y <= thisyear) return y;
  } catch (_) {}
  return thisyear;
}

function App() {
  const [standings, setStandings] = useState([]);
  const [year, setYear] = useState(() => getStoredYear());
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    try { localStorage.setItem(YEAR_STORAGE_KEY, String(year)); } catch (_) {}
  }, [year]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') setRefreshKey((k) => k + 1);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => { document.title = 'My Game 3G'; }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const getStandings = async () => {
      const stands = [];
      const yearStr = getyear();
      let rawList;
      try {
        rawList = await Storage.list('standings/' + yearStr);
      } catch (e) {
        if (!cancelled) setLoadError(e?.message || 'No credentials');
        setLoading(false);
        return;
      }
      const fileList = Array.isArray(rawList)
        ? rawList
        : (rawList?.items ?? rawList?.results ?? []);
      if (fileList.length > 0 && fileList[0].lastModified && !cancelled) {
        const diffMs = new Date() - new Date(fileList[0].lastModified);
        const diffMins = Math.round(diffMs / 1000 / 60);
        setMessage(`Last updated ${diffMins} min ago`);
      }
      const count = fileList.length;
      let hadError = false;
      for (let i = 0; i < count; i++) {
        try {
          const url = await Storage.get('standings/' + yearStr + '/' + i + '.csv');
          const resp = await fetch(url, { cache: 'no-store' });
          if (!resp.ok) throw new Error(resp.statusText);
          const stand = await resp.text();
          if (stand !== '' && !cancelled) stands.push(stand);
        } catch (err) {
          if (!cancelled) hadError = true;
          console.log('error getting standings', err);
        }
      }
      if (!cancelled) {
        setStandings(stands);
        if (stands.length === 0 && hadError) setLoadError('No credentials');
        setLoading(false);
      }
    };
    getStandings();
    return () => { cancelled = true; };
  }, [year, refreshKey]);

  const openIditarodStandings = () => {
    window.open('https://iditarod.com/race/' + getyear() + '/standings/');
  };
  const openRaceMap = () => {
    window.open('https://iditarod.com/race-map');
  };
  const getyear = () => JSON.stringify(year);

  return (
    <div className="app-shell">
      <a href="#main" className="skip-link">Skip to standings</a>
      <div className="app-container">
        <header className="app-header">
          <h1 className="app-title">MY GAME 3G</h1>
          <p className="app-subtitle">Iditarod standings and race info</p>
        </header>

        <a href={X_PROFILE_URL} target="_blank" rel="noopener noreferrer" className="x-follow-card">
          <span className="x-follow-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </span>
          <span className="x-follow-text">Follow @The_Iditarod on X for updates</span>
        </a>

        <div className="app-links">
          <button type="button" className="app-link" onClick={openIditarodStandings}>
            Iditarod Standings Page
          </button>
          <button type="button" className="app-link" onClick={openRaceMap}>
            Race Map
          </button>
        </div>

        <main id="main" className="app-main">
          <div className="year-selector-wrap">
            <span className="year-selector-label">Year</span>
            <Dropdown
              options={options}
              onChange={(v) => setYear(v.value)}
              value={getyear()}
              placeholder="Select a year"
            />
          </div>

          {message && <p className="last-updated">{message}</p>}

          {loading && standings.length === 0 && !loadError && (
            <p className="loading-hint">Loading standings…</p>
          )}
          {!loading && standings.length === 0 && !loadError && (
            <p className="empty-hint">No standings for {year}. Try another year.</p>
          )}
          {loadError && (
            <div className="load-error">
              <p className="load-error-title">Unable to load standings</p>
              <p className="load-error-detail">
                {loadError === 'No credentials'
                  ? 'Amplify is not configured for guest access.'
                  : loadError}
              </p>
            </div>
          )}

          {standings.length > 0 && (
            <RaceChart csvString={buildChartCsv(standings)} year={year} />
          )}

          {(() => {
            let rankOffset = 0;
            return standings.map((standing, index) => {
              const type = detectSectionType(standing);
              if (type === 'empty') return null;
              const isRacingOrFinished = type === 'racing' || type === 'finished';
              const thisOffset = rankOffset;
              if (isRacingOrFinished) {
                rankOffset += parseCSV(standing).rows.length;
              }
              return (
                <section key={index} className="section-card">
                  <h2 className="section-title">{sectionTitle(type)}</h2>
                  <StandingsTable csvString={standing} isFirst={isRacingOrFinished} startRank={thisOffset} year={year} />
                </section>
              );
            });
          })()}
        </main>
      </div>
    </div>
  );
}

export default App;
